import type { ReportExclusionOptions, ReportExclusionParams, ReportOptions } from '../../shared/types';
import type { IncludeFilterOption, IncludeFixPlanFilterOption, ReportLogoAction } from './types';
import { post, put } from '../../../../../redux/api';
import { window } from '../../../../../globals';
import type {
  UpdateOrganizationDefaultReportCustomTextRequest,
  UpdateOrganizationDefaultReportCustomTextResponse,
} from '../../../../../../typings/api';
import type { FixPlansParams } from '../../../../../../shared/types/routes/fixPlans';
/**
 * Take the 'includes' choices from a V2 Report Generation preview and make their canonical URL params
 */
export function buildIncludeParams(includeOptions: IncludeFilterOption[]): ReportOptions {
  return Object.fromEntries(includeOptions.map((option) => [option, true]));
}

/**
 * Take the 'excludes' choices from a V2 Report Generation preview and make their canonical URL params
 * in the form ?excludeFields[packageLabels][0]=<label1>&excludeFields[packageLabels][1]=<label2>...
 */
export function buildExcludeParams(excludeOptions: ReportExclusionOptions): ReportExclusionParams {
  const params: ReportExclusionParams = { excludeFields: {} };
  if (excludeOptions.packageLabels) {
    params.excludeFields = {
      packageLabels: excludeOptions.packageLabels,
    };
  }
  return params;
}

/**
 * Take the 'includes' options for Fix Plan report and translate them into 'excludes' URL params
 */
export function buildFixPlanParams(
  includeOptions: IncludeFixPlanFilterOption[],
): FixPlansParams {
  const params: FixPlansParams = {};

  // if includeX is not in the list of options, put excludeX into the params
  if (!includeOptions.includes('includeQuickWins')) {
    params.excludeQuickWins = true;
  }

  if (!includeOptions.includes('includeHighPriority')) {
    params.excludeHighPriority = true;
  }

  if (!includeOptions.includes('includeLowPriority')) {
    params.excludeLowPriority = true;
  }

  if (!includeOptions.includes('includeOutdatedDependencies')) {
    params.excludeOutdatedDependencies = true;
  }

  if (includeOptions.includes('includeTransitiveVulns')) {
    params.includeTransitiveVulns = true;
  }

  if (includeOptions.includes('deduplicateOutdatedDeps')) {
    params.deduplicateOutdatedDeps = true;
  }

  return params;
}

/**
 * Update the report settings (upload/remove log and set custom text)
 */
export async function updateReportSettings({
  shouldSetDefault,
  file,
  reportLogoAction,
  shouldPropagate,
  customText,
  updateReportCustomTextEndpoint,
}: {
  shouldSetDefault: boolean;
  file: File | null;
  reportLogoAction: ReportLogoAction | null;
  customText: string;
  shouldPropagate: boolean;
  updateReportCustomTextEndpoint: string;
}): Promise<void> {
  const organizationId = window.user?.organization?.id;
  if (reportLogoAction === 'SAVE' && file) {
    const { data } = await post<{ filename: string; contentType: string }, { signedUrl: string }>(
      '/api/organization/logo/get_signed_url',
      { filename: file.name, contentType: file.type },
    );

    if (!data) {
      throw new Error('Failed to get signed URL');
    }

    await fetch(data.signedUrl, {
      method: 'PUT',
      body: file,
    });

    await post('/api/organization/logo', { filename: file.name });
    if (window.user && window.user.organization) {
      window.user.organization.image = file.name;
    }
  }

  if (reportLogoAction === 'REMOVE') {
    await post('/api/organization/logo', { unset: true });
    if (window.user && window.user.organization) {
      window.user.organization.image = undefined;
    }
  }

  if (shouldSetDefault) {
    await put<UpdateOrganizationDefaultReportCustomTextRequest, UpdateOrganizationDefaultReportCustomTextResponse>(
      `/api/organizations/${organizationId}/defaultReportCustomText`,
      {
        shouldPropagate,
        reportCustomText: customText,
      },
    );
  }
  await put(updateReportCustomTextEndpoint, { reportCustomText: customText });
}
